import React from "react";
import {
  DropdownInput,
  IOption,
  MultiSelectList,
  QuestionTooltip,
  QuestionTooltipProps,
} from "component-library";
import { i18n } from "i18n";
import _uniqBy from "lodash/uniqBy";
import { ControlSection } from "../../../../../Common";
import {
  IVocalViewModel,
  FacetsViewModel,
  FacetViewModel,
} from "../../../../models/views";
import {
  withEditBasicInfoContext,
  IEditBasicInfoContextProps,
} from "../../../../../../contexts/EditBasicInfoContext";

export const MUSICAL_NOTES: Readonly<IOption<number>[]> = Object.freeze<
  IOption<number>[]
>([
  { value: 1, name: "A0" },
  { value: 2, name: "A♯0/B♭0" },
  { value: 3, name: "B0" },
  { value: 4, name: "C1" },
  { value: 5, name: "C♯1/D♭1" },
  { value: 6, name: "D1" },
  { value: 7, name: "D♯1/E♭1" },
  { value: 8, name: "E1" },
  { value: 9, name: "F1" },
  { value: 10, name: "F♯1/G♭1" },
  { value: 11, name: "G1" },
  { value: 12, name: "G♯1/A♭1" },
  { value: 13, name: "A1" },
  { value: 14, name: "A♯1/B♭1" },
  { value: 15, name: "B1" },
  { value: 16, name: "C2" },
  { value: 17, name: "C♯2/D♭2" },
  { value: 18, name: "D2" },
  { value: 19, name: "D♯2/E♭2" },
  { value: 20, name: "E2" },
  { value: 21, name: "F2" },
  { value: 22, name: "F♯2/G♭2" },
  { value: 23, name: "G2" },
  { value: 24, name: "G♯2/A♭2" },
  { value: 25, name: "A2" },
  { value: 26, name: "A♯2/B♭2" },
  { value: 27, name: "B2" },
  { value: 28, name: "C3" },
  { value: 29, name: "C♯3/D♭3" },
  { value: 30, name: "D3" },
  { value: 31, name: "D♯3/E♭3" },
  { value: 32, name: "E3" },
  { value: 33, name: "F3" },
  { value: 34, name: "F♯3/G♭3" },
  { value: 35, name: "G3" },
  { value: 36, name: "G♯3/A♭3" },
  { value: 37, name: "A3" },
  { value: 38, name: "A♯3/B♭3" },
  { value: 39, name: "B3" },
  { value: 40, name: "C4" },
  { value: 41, name: "C♯4/D♭4" },
  { value: 42, name: "D4" },
  { value: 43, name: "D♯4/E♭4" },
  { value: 44, name: "E4" },
  { value: 45, name: "F4" },
  { value: 46, name: "F♯4/G♭4" },
  { value: 47, name: "G4" },
  { value: 48, name: "G♯4/A♭4" },
  { value: 49, name: "A4" },
  { value: 50, name: "A♯4/B♭4" },
  { value: 51, name: "B4" },
  { value: 52, name: "C5" },
  { value: 53, name: "C♯5/D♭5" },
  { value: 54, name: "D5" },
  { value: 55, name: "D♯5/E♭5" },
  { value: 56, name: "E5" },
  { value: 57, name: "F5" },
  { value: 58, name: "F♯5/G♭5" },
  { value: 59, name: "G5" },
  { value: 60, name: "G♯5/A♭5" },
  { value: 61, name: "A5" },
  { value: 62, name: "A♯5/B♭5" },
  { value: 63, name: "B5" },
  { value: 64, name: "C6" },
  { value: 65, name: "C♯6/D♭6" },
  { value: 66, name: "D6" },
  { value: 67, name: "D♯6/E♭6" },
  { value: 68, name: "E6" },
  { value: 69, name: "F6" },
  { value: 70, name: "F♯6/G♭6" },
  { value: 71, name: "G6" },
  { value: 72, name: "G♯6/A♭6" },
  { value: 73, name: "A6" },
  { value: 74, name: "A♯6/B♭6" },
  { value: 75, name: "B6" },
  { value: 76, name: "C7" },
  { value: 77, name: "C♯7/D♭7" },
  { value: 78, name: "D7" },
  { value: 79, name: "D♯7/E♭7" },
  { value: 80, name: "E7" },
  { value: 81, name: "F7" },
  { value: 82, name: "F♯7/G♭7" },
  { value: 83, name: "G7" },
  { value: 84, name: "G♯7/A♭7" },
  { value: 85, name: "A7" },
  { value: 86, name: "A♯7/B♭7" },
  { value: 87, name: "B7" },
  { value: 88, name: "C8" },
]);

const voiceStyleField = "voiceStyle";

export interface IVocalContent {
  vocalSectionName: string;
  vocalDescription: string;
  vocalLink: string;
  vocalLinkName: string;
  vocalAgeSectionName: string;
  singingRageSectionName: string;
  voiceQualityField: string;
  voiceQualityPlaceholder: string;
  voiceCharacterField: string;
  voiceCharacterPlaceholder: string;
  vocalAgeFromField: string;
  vocalAgeFromPlaceholder: string;
  vocalAgeToField: string;
  vocalAgeToPlaceholder: string;
  lowerNoteField: string;
  lowerNotePlaceholder: string;
  upperNoteField: string;
  upperNotePlaceholder: string;
  beltNoteField: string;
  beltNotePlaceholder: string;
  vocalAgeTooltip: QuestionTooltipProps;
  years: string;
  year: string;
}

export interface IVocalProps extends IEditBasicInfoContextProps {
  model: IVocalViewModel;
  facets: FacetsViewModel;
  content: IVocalContent;
  handleChanges: (model: IVocalViewModel) => void;
  minYearValue: number;
  maxYearValue: number;
}

export class Vocal extends React.Component<IVocalProps> {
  public render() {
    const {
      content,
      sharedState,
      facets: { beltNotes, voiceStyle },
      model,
      handleChanges,
    } = this.props;

    const description = this.renderSectionDescription();
    const selectedVoiceStyle = (model[voiceStyleField] || []).map((facet) => ({
      value: facet.id,
      name: facet.name,
    }));

    return (
      <ControlSection
        name={i18n.t("common:profile.voice.voiceStyle.sectionTitle")}
        description={description}
      >
        <div className="c-additional-info-form__vocal">
          <div className="c-additional-info-form__control-grid-2">
            <MultiSelectList
              type="dropdown"
              content={{
                label: i18n.t("common:profile.voice.voiceStyle.label"),
                placeholder: i18n.t("common:placeholder.pleaseSelect"),
              }}
              isDisabled={selectedVoiceStyle.length >= 3}
              name={voiceStyleField}
              selectedItems={selectedVoiceStyle}
              list={this.mapFacetsToOptions(voiceStyle.facets)}
              handleChanges={(value) => {
                handleChanges({
                  ...model,
                  voiceStyle: _uniqBy(
                    value.map((option) => ({
                      id: option.value,
                      name: option.name,
                    })),
                    "id"
                  ),
                });
              }}
            />
          </div>
          {sharedState.isVisibleSection("vocalAge") && this.renderVocalAge()}
          <div className="c-additional-info-form__subsection">
            {content.singingRageSectionName}
          </div>
          <div className="c-additional-info-form__control-grid-4">
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropdown(
                "lowerNote",
                this.getNotesList(
                  content.lowerNotePlaceholder,
                  0,
                  model.upperNote
                ),
                {
                  label: content.lowerNoteField,
                  placeholder: content.lowerNotePlaceholder,
                }
              )}
            </div>
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropdown(
                "upperNote",
                this.getNotesList(
                  content.upperNotePlaceholder,
                  model.lowerNote
                ),
                {
                  label: content.upperNoteField,
                  placeholder: content.upperNotePlaceholder,
                }
              )}
            </div>
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropdown(
                "beltNote",
                this.mapFacetsToOptions(
                  beltNotes.facets,
                  content.beltNotePlaceholder
                ),
                {
                  label: content.beltNoteField,
                  placeholder: content.beltNotePlaceholder,
                }
              )}
            </div>
          </div>
        </div>
      </ControlSection>
    );
  }

  private renderVocalAge = () => {
    const { content } = this.props;

    const vocalAgeAnnex = <QuestionTooltip {...content.vocalAgeTooltip} />;

    return (
      <React.Fragment>
        <div className="c-additional-info-form__subsection">
          {content.vocalAgeSectionName}
        </div>
        <div className="c-additional-info-form__control-grid-4">
          <div className="c-additional-info-form__control-grid-cell">
            {this.renderDropdown("vocalAgeFrom", this.getFromYearList(), {
              label: content.vocalAgeFromField,
              placeholder: content.vocalAgeFromPlaceholder,
              annex: vocalAgeAnnex,
            })}
          </div>
          <div className="c-additional-info-form__control-grid-cell">
            {this.renderDropdown("vocalAgeTo", this.getToYearList(), {
              label: content.vocalAgeToField,
              placeholder: content.vocalAgeToPlaceholder,
            })}
          </div>
        </div>
      </React.Fragment>
    );
  };

  private mapFacetsToOptions = (
    facets: FacetViewModel[],
    placeholder?: string
  ) => {
    const options: IOption<number>[] = [
      ...facets.map((facet) => ({ name: facet.name, value: facet.id })),
    ];

    return placeholder
      ? [this.getEmptyNumberOption(placeholder), ...options]
      : options;
  };

  private renderDropdown(
    name: string,
    options: IOption<any>[],
    props: { label: string; placeholder: string; annex?: any }
  ) {
    return (
      <DropdownInput<any>
        id={`vocal${name}`}
        variant="select"
        options={options}
        name={name}
        {...props}
        value={this.props.model[name]}
        valueChanged={this.dropdownChanged(name)}
      />
    );
  }

  private renderSectionDescription = () => {
    const { vocalDescription, vocalLink, vocalLinkName } = this.props.content;

    return (
      <div>
        {vocalDescription}&nbsp;
        <a href={vocalLink} target="_blank" tabIndex={0} rel="noreferrer">
          {vocalLinkName}
        </a>
      </div>
    );
  };

  private dropdownChanged = (fieldName: string) => {
    return (value: any) => {
      const updatedModel = { ...this.props.model };

      updatedModel[fieldName] = Number.isNaN(value) ? null : value;

      if (updatedModel[fieldName] != this.props.model[fieldName]) {
        this.props.handleChanges(updatedModel);
      }
    };
  };

  private getFromYearList = (): IOption<number>[] => {
    const { model, minYearValue, maxYearValue } = this.props;
    const maxFromYearValue =
      model.vocalAgeTo != undefined && Number.isInteger(model.vocalAgeTo)
        ? model.vocalAgeTo
        : maxYearValue;

    const list: IOption<number>[] = [
      this.getEmptyNumberOption(this.props.content.vocalAgeFromPlaceholder),
    ];

    for (let year = minYearValue; year <= maxFromYearValue; year++) {
      list.push({ value: year, name: this.getYearLabel(year) });
    }

    return list;
  };

  private getToYearList = (): IOption<number>[] => {
    const { model, minYearValue, maxYearValue } = this.props;
    const minToYearValue =
      model.vocalAgeFrom != undefined && Number.isInteger(model.vocalAgeFrom)
        ? model.vocalAgeFrom
        : minYearValue;

    const list: IOption<number>[] = [
      this.getEmptyNumberOption(this.props.content.vocalAgeToPlaceholder),
    ];

    for (let year = minToYearValue; year <= maxYearValue; year++) {
      list.push({ value: year, name: this.getYearLabel(year) });
    }

    return list;
  };

  private getNotesList = (
    placeholder: string,
    fromValue: number = 0,
    toValue: number = 0
  ): IOption<number>[] => {
    if (!fromValue) {
      fromValue = MUSICAL_NOTES[0].value;
    }

    if (!toValue) {
      toValue = MUSICAL_NOTES[MUSICAL_NOTES.length - 1].value;
    }

    const firstIndex =
      MUSICAL_NOTES.findIndex((note) => note.value === fromValue) || 0;
    const lastIndex =
      (MUSICAL_NOTES.findIndex((note) => note.value === toValue) ||
        MUSICAL_NOTES.length - 1) + 1;

    const options = [
      this.getEmptyNumberOption(placeholder),
      ...MUSICAL_NOTES.slice(firstIndex, lastIndex),
    ];

    return options;
  };

  private getEmptyNumberOption = (placeholder: string): IOption<number> => ({
    value: NaN,
    name: placeholder,
  });

  private getYearLabel = (age: number) =>
    `${age} ${age > 1 ? this.props.content.years : this.props.content.year}`;
}

export default withEditBasicInfoContext(Vocal);
